// useBandTeam (parity #10) — the band-seat team-management hook (the artist-side crew card), the
// twin of useTeams: built on useBandQuery/useBandMutation so the key is ALWAYS ['band', bandId, 'team'] (the
// tenant in the key, the structural fix). GET /team is band:manage-only (an active AV seat), so the
// crew card only mounts for a manager. Every mutation surfaces the server error verbatim (403 self-change /
// owner-seat, 400 unknown role, 403/409 remove-self/owner) and NEVER reports success on an unpersisted write.
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext, useBandContextOrNull } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type BandTeamRead = components["schemas"]["BandTeamRead"];
type BandSeatView = components["schemas"]["BandSeatView"];
type PendingInvitesRead = components["schemas"]["PendingInvitesRead"];
type PendingInvite = components["schemas"]["PendingInvite"];
type InviteIssued = components["schemas"]["InviteIssued"];
type InviteBandSeatRequest = components["schemas"]["InviteBandSeatRequest"];

export type { BandTeamRead, BandSeatView, PendingInvitesRead, PendingInvite, InviteIssued, InviteBandSeatRequest };

/**
 * Fallback labels for the band ROLE keys — NOT a picker list, and no longer exported.
 *
 * ⚠️ IT WAS BOTH, AND THAT IS WHAT BROKE. This array fed the crew invite and role-change pickers until
 * 2026-09-04, by which time the titles split (798/799) had made five of its eight values invalid: the gates
 * take a TITLE and resolve the role themselves, so picking "Band Member" — the commonest crew invite — was a
 * 422, as was every production seat, whose three role keys 799 had collapsed into `production_crew`.
 *
 * Both pickers now read the server catalog (`GET /me/seat-roles` → `bandRoles`, which IS core.seat_titles).
 * What is left here is the last-resort label for a row carrying a role and no title: a job LABEL is resolved
 * from the TITLE, never the role, because 70 titles collapse onto 22 roles and a role names the wrong job —
 * all 11 production titles grant `production_crew`. Keep this in sync with core.seat_roles, and do not
 * export it: an exported "roles with labels" array is indistinguishable from a picker source.
 */
const ROLE_FALLBACK_LABELS: Record<string, string> = {
  // ART-171: just "Band Member" — the crimson AV mark (drawn by RoleLabel / AvMark beside this text) is the
  // "full access" signal, so the words never carry "(AV)" / "(Full Access)". A picker option that renders both
  // this string AND the mark would otherwise read "Band Member (AV) [mark]".
  band_member_av: "Band Member",
  band_member: "Band Member",
  tour_manager: "Tour Manager",
  production_crew: "Production Crew",
  crew_general: "Crew",
  personal_assistant: "Personal Assistant",
  content: "Content",
  merch_manager: "Merch Manager",
};

/**
 * Acronym seats whose plain title-casing reads wrong ("Foh Systems Tech"). The server owns the role
 * vocabulary and can ship a seat this client has never heard of, so these sit outside the fallback map above.
 */
const ROLE_ACRONYMS: Record<string, string> = {
  foh_engineer: "FOH Engineer",
  foh_systems_tech: "FOH Systems Tech",
  monitor_systems_tech: "Monitor Systems Tech",
  monitor_engineer: "Monitor Engineer",
  rf_tech: "RF Tech",
  pa_tech_1: "PA Tech 1",
  pa_tech_2: "PA Tech 2",
  av_tech: "AV Tech",
};

/** `some_role_key` → `Some Role Key`. */
function titleCaseSlug(slug: string): string {
  return slug
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Display text for a seat role. A role this client doesn't know must never reach the screen as a raw
 * `snake_case` key — the server owns the vocabulary, so unknown slugs get the acronym override if one
 * exists and are otherwise underscore-stripped and title-cased.
 */
export function bandRoleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  const known = ROLE_FALLBACK_LABELS[role];
  if (known) return known;
  return ROLE_ACRONYMS[role] ?? titleCaseSlug(role);
}

interface TeamSnapshot {
  previous?: BandTeamRead;
}

/** Exported for the one cross-feature invalidation: closing the self-profile EDITOR refreshes the
 * crew read, whose `displayName` fed the profile modal's hero. */
export const teamKey = (bandId: string) => ["band", bandId, "team"] as const;

function rollback(qc: QueryClient, key: readonly unknown[], previous: BandTeamRead | undefined): void {
  if (previous) qc.setQueryData(key, previous);
  else void qc.invalidateQueries({ queryKey: key });
}

// ---- queries ------------------------------------------------------------------------------------------

/** `GET /bands/{bandId}/team` — the band's active crew seats. band:manage-only (a 403 → error state).*/
export function useBandTeam(enabled = true) {
  const { api } = useApp();
  return useBandQuery<BandTeamRead>(
    ["team"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/team", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled },
  );
}

/**
 * The active band's crew list, or an EMPTY list when no band is mounted.
 *
 * Context-SAFE, which is the whole reason it exists: `useBandTeam` runs on `useBandQuery`, and that uses the
 * THROWING band context, so it cannot be called from anything that also renders outside a band (the shell's
 * switcher, the org home, a person profile opened from the roster's Team list). This runs the identical
 * query under the identical key, so it shares one cache entry with `useBandTeam` and costs no extra request
 * wherever the team is already loaded, and simply stays disabled when there is no band.
 *
 * "No band" is not an error state and must not render as one: the caller gets an empty roster and shows the
 * seat-derived controls as ABSENT, exactly as it already does for a person who holds no seat in the band
 * that IS mounted. The two are the same situation from the surface's point of view.
 */
export function useBandTeamOrNull(enabled = true): { items: BandSeatView[]; isLoading: boolean } {
  const { api } = useApp();
  const band = useBandContextOrNull();
  const bandId = band?.bandId;
  const q = useQuery<BandTeamRead, Error>({
    queryKey: ["band", bandId, "team"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/team", {
        params: { path: { bandId: bandId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 30_000,
    // BOTH: a band has to be mounted, AND the caller has to want this list. The second half is for callers
    // that read the crew only on one of several sources (`CrewInviteePicker`) — without it they fetch a team
    // they will not render every time they happen to mount inside a band.
    enabled: enabled && !!bandId,
  });
  // `isLoading` is false without a band — a disabled query is idle, not pending, and a caller that spins on
  // it would spin forever under the org shell.
  return { items: q.data?.items ?? [], isLoading: enabled && !!bandId && q.isLoading };
}

/**
 * The band OWNER's personId, or undefined — for surfaces that must tell "this person" from "the band".
 * Context-safe via {@link useBandTeamOrNull}, so it shares that one cache entry.
 */
export function useBandOwnerPersonId(): string | undefined {
  return useBandTeamOrNull().items.find((s) => s.isOwner)?.personId ?? undefined;
}

/** `GET /bands/{bandId}/team/invites` — the band's pending crew invites (copy-link, read-only).*/
export function useBandSeatInvites(enabled = true) {
  const { api } = useApp();
  return useBandQuery<PendingInvitesRead>(
    ["team", "invites"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/team/invites", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled },
  );
}

// ---- mutations ----------------------------------------------------------------------------------------

/**
 * `PUT /bands/{bandId}/team/{seatId}/role` ({role}) → 204 (parity #10, `change_band_seat_role`). Optimistic
 * re-label with rollback + toast. The gate guards surface verbatim: 403 on self-change (lockout) or the owner
 * seat, 400 on an unknown role — the card renders the owner row read-only and never role-edits the caller's own.
 */
export function useChangeSeatRole() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = teamKey(bandId);

  return useBandMutation<void, { seatId: string; title: string }, TeamSnapshot>(
    async (band, { seatId, title }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/team/{seatId}/role", {
        params: { path: { bandId: band, seatId } },
        body: { title },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async ({ seatId, title }) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<BandTeamRead>(key);
        qc.setQueryData<BandTeamRead>(key, (old) =>
          // Only the TITLE is set optimistically: the role is derived server-side from it, so the client
          // cannot know the new one without guessing. The invalidate below settles it a moment later.
          old ? { items: (old.items ?? []).map((s) => (s.seatId === seatId ? { ...s, titleKey: title } : s)) } : old,
        );
        return { previous };
      },
      onError: (err, _v, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't change the title"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/**
 * `DELETE /bands/{bandId}/team/{seatId}` → 204 (`remove_band_seat`). Optimistic remove with rollback + toast;
 * the gate forbids removing the owner seat (403) and a manager can't remove themselves via this admin surface.
 */
/**
 * `PUT /bands/{bandId}/team/{seatId}/acting-role` — set the display-only "acting as" label, or CLEAR it by
 * sending null ("Clear role" in the menu is this same call, not a second endpoint).
 *
 * Optimistic like the role change, and for a better reason: the label grants nothing, so there is no
 * capability to be briefly wrong about. A 403 comes from the GATE (TM or AV), not the route.
 */
export function useSetActingRole() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = teamKey(bandId);

  return useBandMutation<void, { seatId: string; actingRole: string | null }, TeamSnapshot>(
    async (band, { seatId, actingRole }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/team/{seatId}/acting-role", {
        params: { path: { bandId: band, seatId } },
        body: { actingRole },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async ({ seatId, actingRole }) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<BandTeamRead>(key);
        qc.setQueryData<BandTeamRead>(key, (old) =>
          old ? { items: (old.items ?? []).map((s) => (s.seatId === seatId ? { ...s, actingRole } : s)) } : old,
        );
        return { previous };
      },
      onError: (err, _v, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't set the acting role"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/**
 * PRO: set a member's touring PARTY, or RESET it to their title's default by sending null ("Reset to
 * default" in the menu is this same call, not a second endpoint) — the acting-label shape exactly.
 *
 * **NOT optimistic, unlike its sibling, and the difference is the point.** An acting label is display only,
 * so a brief wrong pill costs nothing. A party decides who may read this person's EMAIL (ART-144 §5.6), and
 * the server recomputes that for the whole list — so painting one row's new party locally would leave every
 * other row's address showing the OLD line until the refetch landed. A 403 comes from the GATE (Production
 * Office by resolved party, or an AV seat), not from the route.
 */
export function useSetCrewParty() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = teamKey(bandId);

  return useBandMutation<void, { seatId: string; party: string | null }>(
    async (band, { seatId, party }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/team/{seatId}/party", {
        params: { path: { bandId: band, seatId } },
        body: { party },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onError: (err) => pushToast(errorMessage(err, "Couldn't set the party")),
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

export function useRemoveSeat() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = teamKey(bandId);

  return useBandMutation<void, string, TeamSnapshot>(
    async (band, seatId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/team/{seatId}", {
        params: { path: { bandId: band, seatId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async (seatId) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<BandTeamRead>(key);
        qc.setQueryData<BandTeamRead>(key, (old) =>
          old ? { items: (old.items ?? []).filter((s) => s.seatId !== seatId) } : old,
        );
        return { previous };
      },
      onError: (err, _id, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't remove the member"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/**
 * `POST /bands/{bandId}/team/invites` ({email, role, firstName?, lastName?}) → 201 InviteIssued (a
 * one-time share token, always returned so the link can be sent by hand if the email never lands).
 * Invalidates the team key, which covers the pending-invites child key — the invite IS the pending row.
 */
/**
 * `GET /team/lookup?q=` — the Add Crew account search. The CALLER debounces and qualifies (a full
 * email with a TLD, or 7+ phone digits) — this hook just runs the query when `q` is non-empty.
 * `retry: false`: a 400 for an underqualified query is a verdict, not a blip.
 */
export function useCrewLookup(q: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  return useQuery({
    queryKey: ["band", bandId, "team", "lookup", q],
    enabled: q.length > 0,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/team/lookup", {
        params: { path: { bandId }, query: { q } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

export function useInviteBandSeat() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = teamKey(bandId);

  return useBandMutation<InviteIssued, InviteBandSeatRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/team/invites", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
      onError: (err) => {
        pushToast(errorMessage(err, "Couldn't send the invite"));
      },
    },
  );
}

/**
 * `DELETE /bands/{bandId}/team/invites/{inviteId}` → 204 — revoke a pending crew invite (the share token
 * dies with it). Optimistic removal from the pending list with rollback + toast; idempotent server-side,
 * and a 409 means the invite was accepted in the meantime — that person is a seat now.
 */
export function useRevokeSeatInvite() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const invitesKey = [...teamKey(bandId), "invites"] as const;

  return useBandMutation<void, string, { previous?: PendingInvitesRead }>(
    async (band, inviteId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/team/invites/{inviteId}", {
        params: { path: { bandId: band, inviteId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async (inviteId) => {
        await qc.cancelQueries({ queryKey: invitesKey });
        const previous = qc.getQueryData<PendingInvitesRead>(invitesKey);
        qc.setQueryData<PendingInvitesRead>(invitesKey, (old) =>
          old ? { items: (old.items ?? []).filter((i) => i.inviteId !== inviteId) } : old,
        );
        return { previous };
      },
      onError: (err, _id, ctx) => {
        if (ctx?.previous) qc.setQueryData(invitesKey, ctx.previous);
        else void qc.invalidateQueries({ queryKey: invitesKey });
        pushToast(errorMessage(err, "Couldn't revoke the invite"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: invitesKey });
      },
    },
  );
}
